import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { superAdminAPI } from '../../lib/api';
import { formatDate, formatCurrency, getStatusColor, getInitials } from '../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { toast } from 'sonner';
import { Pagination, PAGE_SIZE } from '../../components/ui/pagination-control';
import { SearchFilterBar } from '../../components/ui/search-filter-bar';
import {
    Users,
    Building2,
    Briefcase,
    FileText,
    TrendingUp,
    Loader2,
    DollarSign,
    Crown,
    PoundSterling,
    CreditCard,
    ArrowUpRight,
    ArrowDownRight,
    Calendar,
    Zap,
    RefreshCw
} from 'lucide-react';

const SuperAdminDashboard = () => {
    const { isAdmin, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    
    // Pagination state
    const [usersPage, setUsersPage] = useState(1);
    const [usersTotal, setUsersTotal] = useState(0);
    const [ordersPage, setOrdersPage] = useState(1);
    const [ordersTotal, setOrdersTotal] = useState(0);
    
    // Search/filter state
    const [usersSearch, setUsersSearch] = useState('');
    const [usersRole, setUsersRole] = useState('');

    // Both admin and super_admin have full access
    const hasFullAccess = isAdmin || isSuperAdmin;

    useEffect(() => {
        if (!hasFullAccess) {
            navigate('/');
            return;
        }
        fetchData();
    }, [hasFullAccess, navigate]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, usersRes, ordersRes] = await Promise.all([
                superAdminAPI.getStats(),
                superAdminAPI.getUsers({ page: 1, limit: PAGE_SIZE }),
                superAdminAPI.getOrders({ page: 1, limit: PAGE_SIZE }),
            ]);
            setStats(statsRes.data);
            setUsers(usersRes.data.users);
            setUsersTotal(usersRes.data.total || 0);
            setUsersPage(1);
            setOrders(ordersRes.data.orders);
            setOrdersTotal(ordersRes.data.total || 0);
            setOrdersPage(1);
            
            // Filter subscribers (non-free users)
            const subs = usersRes.data.users.filter(u => 
                u.subscription_tier && u.subscription_tier !== 'free'
            );
            setSubscribers(subs);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };
    
    const fetchUsers = async (page, search = usersSearch, role = usersRole) => {
        try {
            const params = { page, limit: PAGE_SIZE };
            if (search) params.search = search;
            if (role) params.role = role;
            const res = await superAdminAPI.getUsers(params);
            setUsers(res.data.users);
            setUsersTotal(res.data.total || 0);
            setUsersPage(page);
            // Update subscribers based on current page
            const subs = res.data.users.filter(u => 
                u.subscription_tier && u.subscription_tier !== 'free'
            );
            setSubscribers(subs);
        } catch (error) {
            toast.error('Failed to load users');
        }
    };
    
    const fetchOrders = async (page) => {
        try {
            const res = await superAdminAPI.getOrders({ page, limit: PAGE_SIZE });
            setOrders(res.data.orders);
            setOrdersTotal(res.data.total || 0);
            setOrdersPage(page);
        } catch (error) {
            toast.error('Failed to load orders');
        }
    };

    const handleUpdateRole = async (userId, newRole) => {
        try {
            await superAdminAPI.updateUserRole(userId, newRole);
            toast.success('User role updated');
            fetchData();
        } catch (error) {
            console.error('Failed to update role:', error);
            toast.error('Failed to update role');
        }
    };

    // Calculate revenue metrics
    const calculateRevenue = () => {
        const paidOrders = orders.filter(o => o.payment_status === 'paid');
        const multiApplyTotal = paidOrders.reduce((sum, o) => sum + (o.price || 0), 0);
        
        // Subscription revenue (monthly recurring)
        const standardSubs = subscribers.filter(s => s.subscription_tier === 'standard').length;
        const proSubs = subscribers.filter(s => s.subscription_tier === 'pro').length;
        const monthlyRecurring = (standardSubs * 2.98) + (proSubs * 9.98);
        
        // This month's orders
        const thisMonth = new Date();
        thisMonth.setDate(1);
        const thisMonthOrders = paidOrders.filter(o => new Date(o.created_at) >= thisMonth);
        const thisMonthRevenue = thisMonthOrders.reduce((sum, o) => sum + (o.price || 0), 0);
        
        return {
            multiApplyTotal,
            monthlyRecurring,
            thisMonthRevenue,
            totalRevenue: multiApplyTotal + monthlyRecurring,
            paidOrdersCount: paidOrders.length,
            thisMonthOrdersCount: thisMonthOrders.length
        };
    };

    const revenue = stats ? calculateRevenue() : {};

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="bg-neutral-50 min-h-screen">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header */}
                    <div className="mb-8 flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                                <Crown className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <h1 className="font-heading text-2xl font-bold text-neutral-900">
                                    Super Admin Dashboard
                                </h1>
                                <p className="text-neutral-500">Complete platform overview and revenue tracking</p>
                            </div>
                        </div>
                        <Button onClick={fetchData} variant="outline" size="sm">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                        </Button>
                    </div>

                    {/* Revenue Section */}
                    <div className="mb-8">
                        <h2 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                            <PoundSterling className="w-5 h-5 text-emerald-600" />
                            Revenue Overview
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {/* Total Revenue */}
                            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-emerald-100 text-sm">Total Revenue</p>
                                            <p className="font-heading text-3xl font-bold mt-1">
                                                {formatCurrency(revenue.totalRevenue || 0)}
                                            </p>
                                        </div>
                                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                                            <TrendingUp className="w-6 h-6" />
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-emerald-400/30">
                                        <div className="flex items-center gap-1 text-sm text-emerald-100">
                                            <ArrowUpRight className="w-4 h-4" />
                                            <span>All time earnings</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Monthly Recurring */}
                            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-blue-100 text-sm">Monthly Recurring</p>
                                            <p className="font-heading text-3xl font-bold mt-1">
                                                {formatCurrency(revenue.monthlyRecurring || 0)}
                                            </p>
                                        </div>
                                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                                            <RefreshCw className="w-6 h-6" />
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-blue-400/30">
                                        <div className="flex items-center gap-1 text-sm text-blue-100">
                                            <span>{subscribers.length} active subscribers</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Multi-Apply Revenue */}
                            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-purple-100 text-sm">Multi-Apply Sales</p>
                                            <p className="font-heading text-3xl font-bold mt-1">
                                                {formatCurrency(revenue.multiApplyTotal || 0)}
                                            </p>
                                        </div>
                                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                                            <Zap className="w-6 h-6" />
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-purple-400/30">
                                        <div className="flex items-center gap-1 text-sm text-purple-100">
                                            <span>{revenue.paidOrdersCount || 0} paid orders</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* This Month */}
                            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-amber-100 text-sm">This Month</p>
                                            <p className="font-heading text-3xl font-bold mt-1">
                                                {formatCurrency(revenue.thisMonthRevenue || 0)}
                                            </p>
                                        </div>
                                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                                            <Calendar className="w-6 h-6" />
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-amber-400/30">
                                        <div className="flex items-center gap-1 text-sm text-amber-100">
                                            <span>{revenue.thisMonthOrdersCount || 0} orders this month</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-neutral-500">Total Users</p>
                                        <p className="font-heading text-xl font-bold">{stats?.totals?.users || 0}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-neutral-500">Agencies</p>
                                        <p className="font-heading text-xl font-bold">{stats?.totals?.agencies || 0}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                                        <Briefcase className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-neutral-500">Orders</p>
                                        <p className="font-heading text-xl font-bold">{stats?.totals?.orders || 0}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                        <FileText className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-neutral-500">Applications</p>
                                        <p className="font-heading text-xl font-bold">{stats?.totals?.applications || 0}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                                        <CreditCard className="w-5 h-5 text-violet-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-neutral-500">Subscribers</p>
                                        <p className="font-heading text-xl font-bold">{subscribers.length}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Subscription Breakdown */}
                    <div className="mb-8">
                        <h2 className="text-lg font-semibold text-neutral-900 mb-4">Subscription Plans</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Card className="border-neutral-200">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <Badge className="bg-neutral-100 text-neutral-700">Free</Badge>
                                        <span className="text-2xl font-bold">{stats?.subscriptions?.free || 0}</span>
                                    </div>
                                    <p className="text-sm text-neutral-500">Basic access users</p>
                                    <div className="mt-3 text-xs text-neutral-400">
                                        Revenue: £0/month
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="border-primary-200 bg-primary-50/50">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <Badge className="bg-primary-100 text-primary-700">Standard</Badge>
                                        <span className="text-2xl font-bold">{stats?.subscriptions?.standard || 0}</span>
                                    </div>
                                    <p className="text-sm text-neutral-500">£2.98/month subscribers</p>
                                    <div className="mt-3 text-sm font-medium text-primary-600">
                                        Revenue: {formatCurrency((stats?.subscriptions?.standard || 0) * 2.98)}/month
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="border-amber-200 bg-amber-50/50">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <Badge className="bg-amber-100 text-amber-700">Pro</Badge>
                                        <span className="text-2xl font-bold">{stats?.subscriptions?.pro || 0}</span>
                                    </div>
                                    <p className="text-sm text-neutral-500">£9.98/month subscribers</p>
                                    <div className="mt-3 text-sm font-medium text-amber-600">
                                        Revenue: {formatCurrency((stats?.subscriptions?.pro || 0) * 9.98)}/month
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Tabs */}
                    <Tabs defaultValue="subscribers" className="space-y-6">
                        <TabsList className="flex-wrap">
                            <TabsTrigger value="subscribers">Subscribers</TabsTrigger>
                            <TabsTrigger value="users">All Users</TabsTrigger>
                            <TabsTrigger value="orders">Multi-Apply Orders</TabsTrigger>
                        </TabsList>

                        {/* Subscribers Tab */}
                        <TabsContent value="subscribers">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-primary-600" />
                                        Active Subscribers ({subscribers.length})
                                    </CardTitle>
                                    <CardDescription>Users with paid subscription plans</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {subscribers.length === 0 ? (
                                        <div className="text-center py-12">
                                            <CreditCard className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                                            <p className="text-neutral-500">No subscribers yet</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>User</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Plan</TableHead>
                                                    <TableHead>Monthly Value</TableHead>
                                                    <TableHead>Status</TableHead>
                                                    <TableHead>Since</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {subscribers.map((user) => (
                                                    <TableRow key={user.user_id}>
                                                        <TableCell>
                                                            <div className="flex items-center gap-3">
                                                                <Avatar className="h-8 w-8">
                                                                    <AvatarImage src={user.picture} />
                                                                    <AvatarFallback className="bg-primary-100 text-primary-700 text-xs">
                                                                        {getInitials(user.name)}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                                <span className="font-medium">{user.name}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-neutral-500">{user.email}</TableCell>
                                                        <TableCell>
                                                            <Badge className={`capitalize ${
                                                                user.subscription_tier === 'pro' ? 'bg-amber-100 text-amber-700' :
                                                                'bg-primary-100 text-primary-700'
                                                            }`}>
                                                                {user.subscription_tier}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="font-medium text-emerald-600">
                                                            {formatCurrency(user.subscription_tier === 'pro' ? 9.98 : 2.98)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                                                        </TableCell>
                                                        <TableCell className="text-neutral-500">
                                                            {formatDate(user.created_at)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Users Tab */}
                        <TabsContent value="users">
                            <Card>
                                <CardHeader>
                                    <CardTitle>All Users ({usersTotal})</CardTitle>
                                    <CardDescription>Manage user roles and subscriptions</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <SearchFilterBar
                                        searchPlaceholder="Search users by name or email..."
                                        onSearch={(val) => { setUsersSearch(val); fetchUsers(1, val, usersRole); }}
                                        filters={[
                                            { key: 'role', label: 'Role', options: [
                                                { value: 'user', label: 'User' },
                                                { value: 'admin', label: 'Admin' },
                                                { value: 'super_admin', label: 'Super Admin' }
                                            ]}
                                        ]}
                                        filterValues={{ role: usersRole }}
                                        onFilterChange={(key, val) => { setUsersRole(val); fetchUsers(1, usersSearch, val); }}
                                    />
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Subscription</TableHead>
                                                <TableHead>Role</TableHead>
                                                <TableHead>Joined</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {users.map((user) => (
                                                <TableRow key={user.user_id}>
                                                    <TableCell>
                                                        <div className="flex items-center gap-3">
                                                            <Avatar className="h-8 w-8">
                                                                <AvatarImage src={user.picture} />
                                                                <AvatarFallback className="bg-primary-100 text-primary-700 text-xs">
                                                                    {getInitials(user.name)}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            <span className="font-medium">{user.name}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-neutral-500">{user.email}</TableCell>
                                                    <TableCell>
                                                        <Badge className={`capitalize ${
                                                            user.subscription_tier === 'pro' ? 'bg-amber-100 text-amber-700' :
                                                            user.subscription_tier === 'standard' ? 'bg-primary-100 text-primary-700' :
                                                            'bg-neutral-100 text-neutral-700'
                                                        }`}>
                                                            {user.subscription_tier || 'free'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={`capitalize ${
                                                            user.role === 'super_admin' ? 'bg-amber-100 text-amber-700' :
                                                            user.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                                                            'bg-neutral-100 text-neutral-700'
                                                        }`}>
                                                            {user.role?.replace('_', ' ') || 'user'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-neutral-500">
                                                        {formatDate(user.created_at)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            value={user.role || 'user'}
                                                            onValueChange={(value) => handleUpdateRole(user.user_id, value)}
                                                        >
                                                            <SelectTrigger className="w-32">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="user">User</SelectItem>
                                                                <SelectItem value="admin">Admin</SelectItem>
                                                                <SelectItem value="super_admin">Super Admin</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <Pagination currentPage={usersPage} totalItems={usersTotal} onPageChange={fetchUsers} />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Orders Tab */}
                        <TabsContent value="orders">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Multi-Apply Orders ({ordersTotal})</CardTitle>
                                    <CardDescription>All multi-apply package purchases</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {orders.length === 0 ? (
                                        <div className="text-center py-12">
                                            <Briefcase className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                                            <p className="text-neutral-500">No orders yet</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Order ID</TableHead>
                                                    <TableHead>User</TableHead>
                                                    <TableHead>Package</TableHead>
                                                    <TableHead>Price</TableHead>
                                                    <TableHead>Applications</TableHead>
                                                    <TableHead>Status</TableHead>
                                                    <TableHead>Date</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {orders.map((order) => (
                                                    <TableRow key={order.order_id}>
                                                        <TableCell className="font-mono text-xs">
                                                            {order.order_id?.slice(0, 16)}...
                                                        </TableCell>
                                                        <TableCell className="text-neutral-600">
                                                            {order.user_id?.slice(0, 12)}...
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge className={`capitalize ${
                                                                order.package === 'pro_plus' ? 'bg-amber-100 text-amber-700' :
                                                                order.package === 'pro' ? 'bg-primary-100 text-primary-700' :
                                                                'bg-neutral-100 text-neutral-700'
                                                            }`}>
                                                                {order.package?.replace('_', ' ')}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="font-semibold text-emerald-600">
                                                            {formatCurrency(order.price || 0)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="text-neutral-600">
                                                                {(order.applications_count || 0) - (order.applications_remaining || 0)} / {order.applications_count || 0}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex gap-2">
                                                                <Badge className={`capitalize border ${
                                                                    order.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                                                    order.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                                                    'bg-amber-100 text-amber-700'
                                                                }`}>
                                                                    {order.status?.replace('_', ' ') || 'pending'}
                                                                </Badge>
                                                                <Badge className={`capitalize border ${
                                                                    order.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                                                    'bg-amber-100 text-amber-700'
                                                                }`}>
                                                                    {order.payment_status || 'pending'}
                                                                </Badge>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-neutral-500">
                                                            {formatDate(order.created_at)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                    <Pagination currentPage={ordersPage} totalItems={ordersTotal} onPageChange={fetchOrders} />
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </Layout>
    );
};

export default SuperAdminDashboard;
